import { ReactNode } from "react";
import { Stack } from "@mui/material";

import { useTranslation } from "react-i18next";

import { useListDevicesDevicesGetQuery } from "api";
import { ErrorPage, LoadingPage, NoDataPage } from "components/pages";
import { SUPPORTED_DEVICE_TYPES } from "constants/deviceConstants";
import { IZone } from "interfaces/zone.interface";
import { mapById } from "utils/mapById";

import { DeviceList } from "./components/DeviceList";

import { PageHeader } from "../components/PageHeader";

type WrapperProps = {
  children: ReactNode;
  title: string;
};

function Wrapper({ children, title }: WrapperProps) {
  return (
    <Stack sx={{ minHeight: "100vh", gap: 2 }}>
      <PageHeader title={title} />
      {children}
    </Stack>
  );
}

type DeviceListPageProps = {
  zone: IZone;
  zones: IZone[];
};

export const DeviceListPage = ({ zone, zones }: DeviceListPageProps) => {
  const { t } = useTranslation();

  const {
    data: deviceData,
    isLoading,
    isError,
    refetch,
  } = useListDevicesDevicesGetQuery({
    path: `${zone.path}.*`,
    types: SUPPORTED_DEVICE_TYPES,
    embed: ["subject"],
  });
  const deviceList =
    deviceData?.data?.filter((d) => d.type !== "generic") ?? [];

  if (isLoading) {
    return (
      <Wrapper title={zone.name}>
        <LoadingPage />
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper title={zone.name}>
        <ErrorPage
          title={"Failed to load devices"}
          buttonOnClick={() => refetch()}
          buttonText={t("Try again")}
        />
      </Wrapper>
    );
  }

  if (deviceList.length === 0) {
    return (
      <Wrapper title={zone.name}>
        <NoDataPage
          message={t("there_are_no_devices_in_this_zone")}
          title={zone.name}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper title={zone.name}>
      <DeviceList devices={deviceList} zoneEntity={mapById(zones)} />
    </Wrapper>
  );
};
